import React, { useState, forwardRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar, SnackbarContent } from 'notistack';
import PropTypes from 'prop-types';

import { styled } from '@mui/material/styles';
import { Collapse, Stack, Paper, Typography, Card, CardActions, IconButton, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import { clearImportedFiles, setRecentlyUploadedFile, cancelAllUploads, cancelUploadById } from 'store/slices/files';
import { Scrollbar } from 'components/Scrollbar';
import { CircularProgress } from '../../common/CircularProgress';
import { UploadCancelDialog } from './UploadCancelDialog';
const Root = styled('div')({
  '@media (min-width:600px)': {
    minWidth: '340px !important',
  },
});

const StyledCard = styled(Card)({
  backgroundColor: '#a7c4e5',
  width: '100%',
  borderRadius: '4px',
});

const StyledTypography = styled(Typography)({
  fontWeight: 500,
});

const ActionRoot = styled(CardActions)({
  padding: '4px 16px 4px 16px',
  justifyContent: 'space-between',
});

const Icons = styled('div')({
  marginLeft: 'auto',
});

const IconWrapper = styled('div')({
  width: 32,
  height: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ExpandIconButton = styled(IconButton)(({ expanded }) => ({
  padding: '8px 8px',
  transform: expanded === 'true' ? 'rotate(180deg)' : 'rotate(0deg)',
  transition: 'all .2s',
  color: '#3b70ac',
}));

const CollapsePaper = styled(Paper)({
  padding: '0px',
  borderRadius: '0px 0px 4px 4px',
});

const FileItemContainer = styled(Stack)({
  padding: '8px 16px',
  minHeight: 40,
  alignItems: 'center',
  '&:hover': {
    backgroundColor: '#f9f9f9',
  },
});

const FileNameTypography = styled(Typography)({
  fontSize: '14px',
  fontWeight: '400',
  width: '250px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const FileItem = ({ file, navigate, cancelUpload }) => {
  const dispatch = useDispatch();

  const [hovered, setHovered] = useState(false);

  const redirectToFileLocation = (file) => {
    dispatch(setRecentlyUploadedFile(file));

    if (!file.parent) {
      navigate(`/files`);
      return;
    }

    if (window.location.pathname === file?.location) {
      return;
    }

    if (file?.location) {
      navigate(file.location);
      return;
    }
  };

  const renderStatusIcon = () => {
    switch (file.status) {
      case 'pending':
        return hovered ? (
          <Tooltip
            title="Cancel upload"
            placement="left"
            arrow
          >
            <IconButton
              size="small"
              onClick={() => cancelUpload(file.id)}
            >
              <CancelOutlinedIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <CircularProgress size={24} />
        );

      case 'fulfilled':
        return hovered ? (
          <Tooltip
            title="Show location"
            placement="left"
            arrow
          >
            <IconButton
              size="small"
              onClick={() => redirectToFileLocation(file)}
            >
              <FolderOpenOutlinedIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <CheckCircleOutlineIcon sx={{ fontSize: 24, color: '#63b982' }} />
        );

      case 'rejected':
        return <HighlightOffIcon sx={{ fontSize: 24, color: '#eb6d48' }} />;

      default:
        return null;
    }
  };

  return (
    <FileItemContainer
      direction="row"
      alignItems="center"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        width="100%"
      >
        <FileNameTypography
          sx={{
            flexGrow: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            marginRight: 1,
          }}
        >
          {file.fileName}
        </FileNameTypography>

        {file.status === 'cancelled' ? (
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Upload cancelled
          </Typography>
        ) : (
          <IconWrapper>{renderStatusIcon()}</IconWrapper>
        )}
      </Stack>
    </FileItemContainer>
  );
};

export const UploadSnackbar = forwardRef(function UploadComplete(props, ref) {
  const importedFiles = useSelector((state) => state.files.importedFiles);
  const { id, message, navigate } = props;
  const dispatch = useDispatch();
  const { closeSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(true);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const handleExpandClick = useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  const uploadMessage = useCallback(() => {
    const hasPendingUpload = importedFiles.some((item) => item.status === 'pending');
    const allUploadCompletedFulfilled = importedFiles.every((item) => item.status === 'fulfilled');
    const allUploadCompleted = importedFiles.every((item) => item.status !== 'pending');
    const totalFailedItems = importedFiles.reduce((total, item) => {
      if (item.status === 'rejected') {
        total += 1;
      }
      return total;
    }, 0);
    const totalFulfilledItems = importedFiles.reduce((total, item) => {
      if (item.status === 'fulfilled') {
        total += 1;
      }
      return total;
    }, 0);
    const totalCancelledItems = importedFiles.reduce((total, item) => {
      if (item.status === 'cancelled') {
        total += 1;
      }
      return total;
    }, 0);

    const hasCancelledUpload = importedFiles.some((item) => item.status === 'cancelled');

    if (importedFiles.length > 0 && allUploadCompleted && hasCancelledUpload) {
      return `${totalCancelledItems} uploads cancelled`;
    }
    if (importedFiles.length > 0 && allUploadCompletedFulfilled) {
      return `${totalFulfilledItems} uploads completed`;
    }
    if (importedFiles.length > 0 && allUploadCompleted && !hasCancelledUpload) {
      return `${totalFulfilledItems} uploads completed (${totalFailedItems} failed)`;
    }
    // if (importedFiles.length > 0 && hasPendingUpload && totalFailedItems > 0) {
    //   return `Uploading ${importedFiles.length} items (${totalFailedItems} failed)`;
    // }
    if (importedFiles.length > 0 && hasPendingUpload) {
      const totalProcessed = importedFiles.filter((f) =>
        ['fulfilled', 'rejected', 'cancelled'].includes(f.status)
      ).length;

      return `Uploading ${totalProcessed} / ${importedFiles.length} items`;
    }
    return message;
  }, [importedFiles, message]);

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
    dispatch(clearImportedFiles());
  }, [id, closeSnackbar, dispatch]);

  const handleCancelUpload = useCallback(
    (fileId) => {
      dispatch(cancelUploadById(fileId));
    },
    [dispatch]
  );

  const handleBatchCancel = useCallback(() => {
    dispatch(cancelAllUploads());
  }, [dispatch]);

  const hasPendingUpload = importedFiles.some((item) => item.status === 'pending');

  return (
    <SnackbarContent ref={ref}>
      <Root>
        <StyledCard>
          <ActionRoot>
            <StyledTypography variant="subtitle2">{uploadMessage()}</StyledTypography>
            <Icons>
              <ExpandIconButton
                size="small"
                aria-label="Show more"
                expanded={expanded.toString()}
                onClick={handleExpandClick}
              >
                <ExpandMoreIcon />
              </ExpandIconButton>
              <IconButton
                size="small"
                aria-label="Close all"
                onClick={() => (hasPendingUpload ? setShowCancelDialog(true) : handleDismiss())}
              >
                <CloseIcon sx={{ width: '0.8em', height: '0.8em' }} />
              </IconButton>
            </Icons>
          </ActionRoot>
          <Collapse
            in={expanded}
            timeout="auto"
            unmountOnExit
          >
            <CollapsePaper>
              <Scrollbar
                sx={{
                  maxHeight: '240px',
                  '& .simplebar-content': {
                    height: '100%',
                  },
                  '& .simplebar-scrollbar:before': {
                    background: '#ebebeb',
                  },
                }}
              >
                {importedFiles.map((file) => (
                  <FileItem
                    key={file.id}
                    file={file}
                    navigate={navigate}
                    cancelUpload={handleCancelUpload}
                  />
                ))}
              </Scrollbar>
            </CollapsePaper>
          </Collapse>
        </StyledCard>
      </Root>
      <UploadCancelDialog
        show={showCancelDialog}
        title="Cancel all uploads?"
        text="Your uploads are not complete. Would you like to cancel all ongoing uploads?"
        continueHandler={() => setShowCancelDialog(false)}
        cancelUploadsHandler={handleBatchCancel}
        closeDialog={() => setShowCancelDialog(false)}
      />
    </SnackbarContent>
  );
});

UploadSnackbar.propTypes = {
  sx: PropTypes.object,
};
